import { EndpointService, type APIPaginatedResponse } from "@/types/api/api";
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import type { AxiosResponse } from "axios";
import { buildQueryKey } from "@/utils/query";
import type { IListRedeemablesParams } from "./Redeemables";

export interface IBatchRequest {
  quantity: number;
  status: number;
  length: number;
  created_at: string;
  id: number;
  prefix: string;
  updated_at: string;
  configuration_id: number;
  batch_metadata: {
    total_batches: number;
    codes_generated: number;
  };
}

export interface IListBatchParams {
  page?: number;
  page_size?: number;
  configuration_id?: number;
  refetchInterval?: number;
  order_by?: string;
}

export interface ICreateBatchRequestParams {
  length: number;
  prefix: string;
  quantity: number;
  configuration_id: number;
}

export interface ICreateCsvGenerationParams {
  csv: string;
  configuration_id: number;
}

export interface IListBatchReponse
  extends APIPaginatedResponse<IBatchRequest> {}

export interface ICodeGenerationService {
  list: (params: IListBatchParams) => UseQueryResult<IListBatchReponse, Error>;
  create: (
    listParams: IListRedeemablesParams,
  ) => UseMutationResult<IBatchRequest, Error, ICreateBatchRequestParams>;
  createCsvGeneration: (
    listParams: IListRedeemablesParams,
  ) => UseMutationResult<IBatchRequest, Error, ICreateCsvGenerationParams>;
}

export class CodeGenerationService implements ICodeGenerationService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params: IListBatchParams): UseQueryResult<IListBatchReponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "code-generation", "list"],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IListBatchReponse>>(
          "/promotions/batch_requests/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return {
          results: data.results,
          next: data.next,
          count: data.count,
          previous: data.previous,
        };
      },
      refetchInterval: params.refetchInterval,
    });
  }

  create(
    listParams: IListRedeemablesParams,
  ): UseMutationResult<IBatchRequest, Error, ICreateBatchRequestParams> {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "code-generation", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IBatchRequest>>(
          "/promotions/batch_requests/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "code-generation", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  createCsvGeneration(
    listParams: IListRedeemablesParams,
  ): UseMutationResult<IBatchRequest, Error, ICreateCsvGenerationParams> {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined && value !== null,
      ),
    );
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "code-generation", "create-csv-generation"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IBatchRequest>>(
          "/promotions/batch_requests/csv",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "code-generation", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }
}
